import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from "react-router";
import { ApplicationState } from '../../store';
import { TeamRole } from "../../store/Models/UserContext";
import { SurveyResponseState } from "../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates";
import * as SurveyResponseController from "../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { ToxnotComponentProperties } from "../core/BaseApplicationComponents/ToxnotComponent";
import { ToxnotSignInRequiredComponent } from "../core/BaseApplicationComponents/ToxnotSignInRequiredComponent";
import { LoadingSpinner, LoadingSpinnerSize } from "../core/LoadingSpinner";
import Layout from "../Layout";
import SurveyResponseProgress from './SurveyResponse/SurveyResponseProgress';
import SurveySidemenu from '../core/SurveySidemenu';
import SurveyPage from '../core/SurveyPage';
import SurveyNavigation from '../core/SurveyNavigation/SurveyNavigation';
import { QuestionnairePage, SurveyPageType } from "../../store/Models/Surveys/SurveyDataModel";
import RevisionRequest from '../core/RevisionRequest/RevisionRequest';
import { ToxnotReactModal, ToxnotReactModalBody } from '../core/ToxnotReactModal';
import SurveyResponseSubmit from './SurveyResponseSubmit/SurveyResponseSubmit';
import { BomValidationStatus, PageQuestionnaireStatuses } from '../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { RegulationManager } from '../../store/Managers/RegulationManager';

import "./SurveyResponseStyles.css";

type PassportParams = {
    importFromPassport: boolean;
}
interface LocalState {
    rerender: boolean;
    dataLoaded: boolean;
}

type SurveyResponseProperties =
    ToxnotComponentProperties &
    RouteComponentProps<{ responseId?: string }> &
    SurveyResponseState &
    typeof SurveyResponseController.actionCreators;

class SurveyResponse extends ToxnotSignInRequiredComponent<SurveyResponseProperties, LocalState> {
    constructor(props: SurveyResponseProperties) {
        super(props);
    }

    public async componentDidMount() {
        const responseId = this.props.match.params.responseId;
        const customeState: PassportParams = this.props.location?.state as PassportParams;

        if (!responseId) {
            this.props.setSurveyResponseState({ showNotFoundErrorScreen: true });
            return;
        }

        if (customeState?.importFromPassport) {
            this.props.setSurveyResponseState({ dataLoaded : false });
        }

        this.setState({
            dataLoaded: false
        });

        // TODO: large-surveys // make sure this is paged
        // TODO: large-surveys // do the validation of the survey as a sequenced operation
        await this.props.getInitialSurveyData(responseId);

        this.setState({
            dataLoaded: true
        });
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    private getSubmitModalHeader = () => (
        <div className="submit-survey-response-modal-header">
            {this.props.coreData?.request?.dataRequested.anyPassportDataAsked ?
                <>
                    <h4 className="modal-title">{this.localizeString("Submit Survey Response & Create a Digital Product Passport")}</h4>
                    <a href={"http://help.exchange.3eco.com/en/articles/5715165-product-passport-exchange-permissions-explained-unlisted-restricted-and-public"} target="_blank">{this.localizeString("Learn More")}</a>
                </> :
                <h4 className="modal-title">{this.localizeString("Submit Survey Response")}</h4>
            }
        </div>
    );

    protected renderConcreteComponent(): JSX.Element | null {
        if (this.props.showErrorMessage) {
            return this.renderErrorMessage(this.localizeString("Sorry, there was an error accessing the service. Please try again later."), false);
        }

        if (this.props.showNotFoundErrorScreen) {
            return this.renderErrorMessage(this.localizeString("Selected survey cannot be found. Please try again."), true);
        }

        if (!this.props.dataLoaded) {
            return (
                <Layout>
                    <div className="tn-section">
                        <div className="text-center">
                            <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                        </div>
                    </div>
                </Layout>
            )
        }

        const navigationProps = {
            onNavigation: this.onNavigation
        };

        return (
            <Layout options={{ pinAppContentToWindowHeight: true, appBodyContentShouldTakeFullWidth: true }}>
                <div className="survey-response-wrapper">
                    <div className="col-xs-12 col-sm-4 col-lg-3 col-xl-2 p-l-10 survey-response-sidemenu-wrapper">
                        <div className="tn-section m-0 m-b-15">
                            <SurveySidemenu />
                        </div>
                    </div>
                    <div className="col-xs-12 col-sm-8 col-lg-9 col-xl-10 p-l-0-imp survey-response-page-wrapper">
                    <SurveyResponseProgress {...this.props.progressState}{...this.props.appServices} />
                        <div className={`tn-section survey-response-content-wrapper ${this.props.coreData?.response.state === 1 ? "survey-response-submitted" : ""}`}>
                            <div className="survey-response-validation-wrapper">
                                { this.props.coreData?.response.state !== 1 && this.renderValidationMessages()}
                            </div>
                            <div className="survey-response-warning-wrapper">
                                {this.renderWarningMessage()}
                            </div>
                            <div className="survey-response-body-wrapper">
                                {this.state?.dataLoaded && this.props.pageDataLoaded ? (
                                    <>
                                        {this.props.navContext.mainNavIndex !== SurveyPageType.SupplierQuestionnaire && (
                                            <RevisionRequest
                                                revisionRequestInstructions={this.props.selectedInventory?.revisionRequest?.generalRevisionInstructions}
                                                revisionRequestTitle={this.localizeString("Revision Instructions:")}
                                                localizer={this.props.appServices}
                                                extensibleColumnNames={this.props.selectedInventory?.extensibleColumnNames ?? []}
                                            />
                                        )}
                                        <SurveyPage />
                                    </>
                                ) : (
                                    <div className="text-center">
                                        <LoadingSpinner size={LoadingSpinnerSize.Medium} />
                                    </div>
                                )}
                            </div>
                            <SurveyNavigation {...navigationProps} />
                        </div>
                    </div>
                    <ToxnotReactModal
                        modalId={"submit-survey-response-modal"}
                        customHeader={this.getSubmitModalHeader()}>
                        <ToxnotReactModalBody>
                            <SurveyResponseSubmit onGoBackButtonClick={() => this.hideModal('submit-survey-response-modal')} />
                        </ToxnotReactModalBody>
                    </ToxnotReactModal>
                </div>
            </Layout>
        );
    }

    private onNavigation = () => {
        this.setState({ rerender: !this.state.rerender });

        document.querySelector(".survey-response-body-wrapper")?.scrollTo(0, 0);
    }

    private renderErrorMessage(errorMessage: string, showBackButton: boolean) {
        return (
            <Layout>
                <div className="tn-section">
                    <p>{errorMessage}</p>
                    {showBackButton && <a href="/Suppliers/SupplierDashboard">{this.localizeString("Go Back")}</a>}
                </div>
            </Layout>
        )
    }

    private renderBomValidationMessages(validationList: BomValidationStatus[]) {
        return (
            validationList !== undefined && validationList !== null && validationList.length > 0 && this.props.selectedInventory?.bomNextClicked &&
            <div className="alert alert-danger">
                    {validationList.filter(v => v.rowIndex === null).map((v, i) => <p key={i}>{v.message}</p>)}
                    {validationList.some(v => v.rowIndex !== null) &&
                        <p key={validationList.length + 1}>
                            <span>{this.props.appServices.localizeString("Some required columns are missing answers. Please see the highlighted cells.")}</span>
                        </p>
                    }
            </div>
        );
    }

    private renderBomNoRowValidationMessages() {
        return (
            this.props.pageDataLoaded &&
            <div className="alert alert-danger">
                <span>{this.props.appServices.localizeString("Please add at least one row in the Ingredient Disclosure.")}</span>
            </div>
        );
    }

    private renderLastPageValidationMessage(invalidFieldsList: string[]) {
        return (
            <div className="alert alert-danger">
                <span>{this.props.appServices.localizeString("Answer required for:")}</span>
                <ul>
                    {invalidFieldsList.map((invalidField, index) => (
                        <li key={index}>{this.props.appServices.localizeString(invalidField.trim())}</li>
                    ))}
                </ul>
            </div>
        );
    }

    private renderQuestionnairePageValidationMessages(validationPage: PageQuestionnaireStatuses) {
        return (
            <div className="alert alert-danger">
                <span>{this.props.appServices.localizeString("Answer required for:")}</span>
                <ul>
                    {validationPage.questions.map((x, i) =>
                        !x.valid &&
                        <li key={`${i}-question`}>
                            <span key={`${i}-text`}>{this.props.appServices.localizeString(x.question.trim())}</span>
                        </li>
                    )}
                </ul>
            </div>
        )
    }

    private getValidationAndCurrentPage(): [PageQuestionnaireStatuses | null, QuestionnairePage | null | undefined] {
        let validationPage = null;
        let currentPage: QuestionnairePage | null | undefined = null;
        const supplierLevelQuestionnaire = SurveyResponseController.getSupplierLevelQuestionnaire();

        if (this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire) {
            currentPage = supplierLevelQuestionnaire?.questionnaire.pages[this.props.navContext.surveyQuestionnairePageIndex];

            validationPage = this.props.validationResult.supplierQuestionnaireStatusList[this.props.navContext.surveyQuestionnairePageIndex];
        } else if (this.props.navContext.mainNavIndex === SurveyPageType.Inventory && this.props.selectedInventory !== null && this.props.selectedInventory !== undefined) {
            currentPage = this.props.selectedInventory?.questionnaireList[this.props.navContext.surveyQuestionnaireIndex]
                ?.pages[this.props.navContext.surveyQuestionnairePageIndex];

            const currentQuestionnaireType = this.props.selectedInventory?.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex]
            const inventory = this.props.validationResult.bomStatusTable[this.props.selectedInventory.inventoryId];

            if (currentPage !== null && currentPage !== undefined) {
                validationPage = SurveyResponseController.getCurrentPageForValidation(inventory, currentQuestionnaireType, currentPage.type, currentPage.title);
            }
        }

        return [validationPage, currentPage];
    }

    private renderValidationMessages() {
        if (this.props.navContext.mainNavIndex === SurveyPageType.Bom && this.props.selectedInventory !== null && this.props.selectedInventory !== undefined) {
            const validationList = this.props.validationResult.bomStatusTable[this.props.selectedInventory.inventoryId]?.inventoryBomValidation;

            if (validationList !== null && validationList !== undefined && validationList.length > 0 && this.props.selectedInventory.bomNextClicked) {
                SurveyResponseController.markBomTableAsRequired();
            } else if (validationList !== null && validationList !== undefined && validationList.length === 0 && this.props.selectedInventory.bomNextClicked && !this.props.validationResult.bomStatusTable[this.props.selectedInventory.inventoryId]?.fieldsStatus) {
                return this.renderBomNoRowValidationMessages();
            }

            return this.renderBomValidationMessages(validationList);
        }

        const [validationPage, currentPage] = this.getValidationAndCurrentPage();
        const hasCurrentPage = validationPage !== undefined && validationPage !== null && currentPage !== undefined && currentPage !== null;

        if (hasCurrentPage && !validationPage.questions.some(x => !x.valid) && currentPage.nextClicked && !SurveyResponseController.isOverallValid(this.props.validationResult) && SurveyResponseController.isLastItem()) {
            const invalidFieldsList: string[] = this.getInvalidCMRTMandatoryFields();
            if ((RegulationManager.ConflictMineralsTemplateRegulationId == currentPage.regulationId) && invalidFieldsList.length > 0) {
                return this.renderLastPageValidationMessage(invalidFieldsList);
            }
        } else if (hasCurrentPage && validationPage.questions.some(x => !x.valid) && currentPage.nextClicked) {
            return this.renderQuestionnairePageValidationMessages(validationPage);
        }
    }

    private getInvalidCMRTMandatoryFields() {
        const invalidMandatoryFieldsList: string[] = [];

        let areFieldsValid = true;
        const companyNameValue = $('#cm_topic_xml_jsoncompany_nametext-input').val();
        const companyEmailValue = $('#cm_topic_xml_jsoncompany_email_contacttext-input').val();
        const q1_Option1DropDown = $('#cm_3tg_added_or_used-Ta').val();
        const q1_Option1Comment = $('#commentcm_3tg_added_or_used-Tatext-input').val();
        const q1_Option2DropDown = $('#cm_3tg_added_or_used-Sn').val();
        const q1_Option2Comment = $('#commentcm_3tg_added_or_used-Sntext-input').val();
        const q1_Option3DropDown = $('#cm_3tg_added_or_used-Au').val();
        const q1_Option3Comment = $('#commentcm_3tg_added_or_used-Autext-input').val();
        const q1_Option4DropDown = $('#cm_3tg_added_or_used-W').val();
        const q1_Option4Comment = $('#commentcm_3tg_added_or_used-Wtext-input').val();
        const q2_Option1DropDown = $('#cm_3tg_remains-Ta').val();
        const q2_Option1Comment = $('#commentcm_3tg_remains-Tatext-input').val();
        const q2_Option2DropDown = $('#cm_3tg_remains-Sn').val();
        const q2_Option2Comment = $('#commentcm_3tg_remains-Sntext-input').val();
        const q2_Option3DropDown = $('#cm_3tg_remains-Au').val();
        const q2_Option3Comment = $('#commentcm_3tg_remains-Autext-input').val();
        const q2_Option4DropDown = $('#cm_3tg_remains-W').val();
        const q2_Option4Comment = $('#commentcm_3tg_remains-Wtext-input').val();

        if (companyNameValue === undefined || companyNameValue === '') {
            invalidMandatoryFieldsList.push(this.props.appServices.localizeString("Company Name"));
        }
        if (companyEmailValue === undefined || companyEmailValue === '') {
            invalidMandatoryFieldsList.push(this.props.appServices.localizeString("Email - Contact"));
        }
        if (q1_Option1DropDown === this.props.appServices.localizeString("Choose one") || (q1_Option1Comment === undefined || q1_Option1Comment === '') ||
            q1_Option2DropDown === this.props.appServices.localizeString("Choose one") || (q1_Option2Comment === undefined || q1_Option2Comment === '') ||
            q1_Option3DropDown === this.props.appServices.localizeString("Choose one") || (q1_Option3Comment === undefined || q1_Option3Comment === '') ||
            q1_Option4DropDown === this.props.appServices.localizeString("Choose one") || (q1_Option4Comment === undefined || q1_Option4Comment === ''))
        {
            invalidMandatoryFieldsList.push(this.props.appServices.localizeString("Is any 3TG intentionally added or used in the product(s) or in the production process?"));
        }
        if (q2_Option1DropDown === this.props.appServices.localizeString("Choose one") || (q2_Option1Comment === undefined || q2_Option1Comment === '') ||
            q2_Option2DropDown === this.props.appServices.localizeString("Choose one") || (q2_Option2Comment === undefined || q2_Option2Comment === '') ||
            q2_Option3DropDown === this.props.appServices.localizeString("Choose one") || (q2_Option3Comment === undefined || q2_Option3Comment === '') ||
            q2_Option4DropDown === this.props.appServices.localizeString("Choose one") || (q2_Option4Comment === undefined || q2_Option4Comment === ''))
        {
            invalidMandatoryFieldsList.push(this.props.appServices.localizeString("Does any 3TG remain in the product(s)?"));
        }
        return invalidMandatoryFieldsList;
    }

    private renderWarningMessage() {
        const [validationPage, currentPage] = this.getValidationAndCurrentPage();
        const hasCurrentPage = validationPage !== undefined && validationPage !== null && currentPage !== undefined && currentPage !== null;
        const extensionRequested = this.props.coreData?.extensionRequest?.inventoryIds?.some(i => i === this.props.selectedInventory?.inventoryId);

        const anyPageNoWithRequiredQuestionNoAnswerAndNoExtensionRequest = !extensionRequested && hasCurrentPage && !validationPage.questions.some(x => !x.valid) &&
            !currentPage?.questions.every(x => x.hidden) &&
            !currentPage.questions.some(q => !SurveyResponseController.doesQuestionNeedValueAndNotHaveValue(q)) &&
            currentPage.nextClicked && (!SurveyResponseController.isLastItem() || SurveyResponseController.isOverallValid(this.props.validationResult))

        if (anyPageNoWithRequiredQuestionNoAnswerAndNoExtensionRequest) {
            return (
                <div className="alert alert-warning">
                    <span>{this.props.appServices.localizeString("Are you sure you want to move on without answering the questions?")}</span>
                </div>
            );
        }
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(withRouter(SurveyResponse) as any);
